import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { GalleryDto, GalleryTheme, GalleryThemeDto } from '@nusign/picflow-backend-types';
import { from, of, switchMap, take } from 'rxjs';
import { unique } from '../../../lib/unique';
import { GalleryThemeService } from '../../../services/gallery-theme.service';
import { GalleryService } from '../../../services/gallery.service';
import { PermissionService } from '../../../services/permission.service';
import { SectionService } from '../../../services/section.service';
import { SubscriptionService } from '../../../services/subscription.service';
import { calcLegacyTheme } from '../../lib/calc-legacy-theme';
import { IsActiveThemePipe } from '../../pipes/is-active-theme.pipe';
import { CustomThemeItemComponent } from '../custom-theme-item/custom-theme-item.component';
import { GallerySettingsCustomizeHeaderComponent } from '../gallery-settings-customize-header/gallery-settings-customize-header.component';
import { GallerySettingsGroupComponent } from '../gallery-settings-group/gallery-settings-group.component';
import { RoundedHoverBoxComponent } from '../rounded-hover-box/rounded-hover-box.component';
import { ThemeItemComponent } from '../theme-item/theme-item.component';

@Component({
  selector: 'app-theme-select-customize',
  imports: [
    ThemeItemComponent,
    GallerySettingsGroupComponent,
    RoundedHoverBoxComponent,
    IsActiveThemePipe,
    GallerySettingsCustomizeHeaderComponent,
    CustomThemeItemComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './theme-select-customize.component.html',
  styleUrl: './theme-select-customize.component.scss',
})
export class ThemeSelectCustomizeComponent {
  private galleryService = inject(GalleryService);
  private subscriptionService = inject(SubscriptionService);
  private permissionService = inject(PermissionService);
  private sectionService = inject(SectionService);
  private galleryThemeService = inject(GalleryThemeService);
  gallery = input.required<GalleryDto>();
  theme = computed(() => {
    const gallery = this.gallery();
    if (!gallery) return;
    return gallery.theme || calcLegacyTheme(gallery);
  });
  back = output<void>();
  customThemes = toSignal(
    this.subscriptionService.getMainPrice().pipe(
      switchMap(() => {
        if (!this.permissionService.canDisplayElement('gallery:themes')) return of([] as GalleryThemeDto[]);
        return this.galleryThemeService.getCustomThemes();
      })
    )
  );
  private standardThemes = toSignal(this.galleryThemeService.getStandardThemes());

  categories = computed(() => {
    const themes = this.standardThemes();
    if (!themes) return [] as { title: string; themes: GalleryTheme[] }[];
    const categories = unique(themes.map(theme => theme.category));
    return categories.map(category => {
      return { title: category, themes: themes.filter(theme => theme.category === category).map(theme => theme.theme) };
    });
  });

  apply(theme: GalleryTheme) {
    from(this.galleryService.updateGallery(this.gallery().id, { theme })).subscribe();
    this.sectionService
      .getSections(this.gallery().id)
      .pipe(take(1))
      .subscribe(sections => {
        sections.forEach(section => {
          this.sectionService
            .updateSection(section, {
              grid_layout: theme.section.grid_layout as 'grid' | 'columns' | 'rows',
              layout_background_color: null,
              show_filenames: null,
              rounded_corners: null,
              width: null,
              spacing: null,
              display_size: null,
              vertical_padding: null,
              text_title_typography: {
                font_color: null,
                font_family: null,
                font_size: null,
                text_alignment: null,
                text_transform: null,
                font_weight: null,
                font_style: null,
                letter_spacing: null,
              },
              text_paragraph_typography: {
                font_color: null,
                font_family: null,
                font_size: null,
                text_alignment: null,
                text_transform: null,
                font_weight: null,
                font_style: null,
                letter_spacing: null,
              },
            })
            .subscribe();
        });
      });
  }
}
